import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  TextInput,
  StyleSheet,
  Platform,
  TouchableOpacity,
  Pressable,
  useWindowDimensions,
  ScrollView,
} from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  AdminTarget,
  addAdminTargetPost,
} from "../../../../store/slices/nhoAdminSlice";
import { RootState, AppDispatch } from "../../../../store";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import FontFamily from "../../../../constants/fonts";
import { useTheme } from "../../../../theme";

interface AdminTargetsTableProps {
  initialData: AdminTarget[];
  onDataChange?: (newData: AdminTarget[]) => void;
  onSaveChanges?: () => void;
  saveLoading?: boolean;
  resetChanges?: boolean;
  onDeleteTarget?: (id?: number, locationName?: string) => void;
  deleteLoading?: boolean;
  deleteSuccess?: boolean;
  addLoading?: boolean;
  addError?: string | null;
  addSuccess?: boolean;
  testID?: string;
}

const AdminTargetsTable: React.FC<AdminTargetsTableProps> = ({
  initialData = [],
  onDataChange,
  onSaveChanges,
  saveLoading = false,
  resetChanges = false,
  onDeleteTarget,
  deleteLoading = false,
  deleteSuccess = false,
  testID = "admin-targets-table",
}) => {
  // Get screen dimensions for responsive layout
  const { width: windowWidth } = useWindowDimensions();
  const isMobile = windowWidth < 500;
  const isIOS = Platform.OS === "ios";
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";


  // Use all rows from initialData
  const initialRows = initialData;

  // Local state to store the editable data
  const [tableData, setTableData] = useState<AdminTarget[]>(initialRows);
  // Track which rows are added by the user vs. from the initial data
  const [userAddedRows, setUserAddedRows] = useState<number[]>([]);
  // Track if data has been changed from initial state
  const [hasChanges, setHasChanges] = useState<boolean>(false);
  // Hover states for web-only hover effects on Add/Delete buttons
  const [isAddHovered, setIsAddHovered] = useState(false);
  const [isDeleteHovered, setIsDeleteHovered] = useState(false);
  // Store the original data for comparison
  const originalDataRef = useRef<AdminTarget[]>([]);
  // Ref to store current tableData for use in effects without triggering re-renders
  const tableDataRef = useRef<AdminTarget[]>(tableData);

  // This effect will only run once on mount to set up the component
  useEffect(() => {
    // Initial setup is handled by useState with initialRows
  }, []);

  // Keep tableDataRef in sync with tableData state
  useEffect(() => {
    tableDataRef.current = tableData;
  }, [tableData]);

  // This effect handles changes from parent when the API data loads or changes
  // We need a ref to track if this is the first load
  const isFirstLoad = useRef(true);
  const prevInitialDataLength = useRef(initialData.length);
  useEffect(() => {
    // Run this effect on the first load OR when the initialData length changes
    // This ensures we catch new rows added through the Redux store
    if (
      (isFirstLoad.current && initialData.length > 0) ||
      initialData.length !== prevInitialDataLength.current
    ) {
      if (isFirstLoad.current) {
        isFirstLoad.current = false;
      }
      prevInitialDataLength.current = initialData.length;

      // Use all rows from initialData
      setTableData(initialData);
      setUserAddedRows([]);
      // Store the original data for comparison (deep copy to avoid reference issues)
      originalDataRef.current = JSON.parse(JSON.stringify(initialData));
      // Reset changes flag
      setHasChanges(false);

      console.log(
        "AdminTargetsTable: Updated tableData from initialData, new length:",
        initialData.length
      );
    }
  }, [initialData]);

  // Effect to handle resetting changes after successful save
  useEffect(() => {
    if (resetChanges) {
      console.log("AdminTargetsTable: Resetting changes after successful save");
      // Update original data reference to match current state
      originalDataRef.current = JSON.parse(
        JSON.stringify(tableDataRef.current)
      );
      // Reset changes flag
      setHasChanges(false);
    }
  }, [resetChanges]); // Removed tableData from dependencies to prevent unnecessary re-renders

  // Effect to handle successful delete operations
  // Note: We no longer make immediate API calls when deleting rows
  // The data will be saved when the user clicks "Save Changes"
  useEffect(() => {
    if (deleteSuccess) {
      console.log("Delete operation completed successfully via Save Changes");
    }
  }, [deleteSuccess]);

  // We're using the same Redux states for save and add operations
  const {
    saveLoading: addApiLoading,
    saveError: addApiError,
    saveSuccess: addApiSuccess,
  } = useSelector((state: RootState) => state.nhoAdmin);

  // Effect to handle API response for add row operation
  useEffect(() => {
    if (addApiSuccess) {
      // The row was successfully added on the server
      // We don't need to update our local state again since we already did it optimistically
      // Just make sure our original data reference is updated
      originalDataRef.current = JSON.parse(
        JSON.stringify(tableDataRef.current)
      );

      // Reset the changes flag since changes are now saved
      setHasChanges(false);
    }

    if (addApiError) {
      // If there was an error, we might want to revert our optimistic update
      // or show an error message
      console.error("Failed to add row:", addApiError);
    }
  }, [addApiSuccess, addApiError]); // Removed tableData from dependencies to prevent unnecessary re-renders

  // Handle text input changes
  const handleInputChange = (
    value: string,
    rowIndex: number,
    field: keyof AdminTarget
  ) => {
    // Ensure value is a valid number or empty
    const numericValue = value.replace(/[^0-9]/g, "");

    // Convert to number, default to 0 if empty
    const newValue = numericValue === "" ? 0 : parseInt(numericValue, 10);

    // Log the change for debugging, especially for GAP_Time_Target
    if (field === "GAP_Time_Target") {
      console.log(`Editing GAP_Time_Target for row ${rowIndex}:`, newValue);
    }

    // Create a new array with the updated value
    const newData = [...tableData];

    // Check if we need to assign an ID to this row (for new rows)
    if (newData[rowIndex].Id === undefined) {
      if (rowIndex > 0 && newData[rowIndex - 1].Id !== undefined) {
        // Generate a new ID by incrementing the previous row's ID
        newData[rowIndex].Id = newData[rowIndex - 1].Id! + 1;
      } else if (
        rowIndex === 0 &&
        tableData.length > 1 &&
        newData[1].Id !== undefined
      ) {
        // First row but we have a second row with ID, use (second row ID - 1)
        newData[rowIndex].Id = newData[1].Id! - 1;
      } else {
        // Fallback ID assignment
        newData[rowIndex].Id = rowIndex + 1;
      }
    }

    // Update the field value
    newData[rowIndex] = {
      ...newData[rowIndex],
      [field]: newValue,
    };

    // Extra check for GAP_Time_Target to ensure it's not lost
    if (field === "GAP_Time_Target") {
      console.log(
        "After update, GAP_Time_Target is now:",
        newData[rowIndex].GAP_Time_Target
      );
    }

    // Update local state
    setTableData(newData);

    // Callback to parent component
    if (onDataChange) {
      onDataChange(newData);
    }

    // Check if data has changes compared to the original
    const isDataChanged =
      JSON.stringify(newData) !== JSON.stringify(originalDataRef.current);
    setHasChanges(isDataChanged);
  };

  // Get access to Redux dispatch
  const dispatch = useDispatch<AppDispatch>();
  // Get the selected location name from Redux store
  const locationName = useSelector(
    (state: RootState) => state.appData.selectedLocation?.displayText
  );

  // Handle adding a new row
  const handleAddRow = () => {
    // Check if we've reached the maximum of 52 rows
    if (tableData.length >= 52) {
      return; // Don't add more rows
    }

    const nextWeekNumber = tableData.length + 1;
    const weekName = `Week${nextWeekNumber}`;

    // Generate a new ID for the new row
    let newRowId: number;

    if (tableData.length > 0) {
      // Generate a new ID by incrementing the last row's ID
      const lastRowId = tableData[tableData.length - 1].Id || 0;
      newRowId = lastRowId + 1;
    } else {
      // Fallback - should never happen with the minimum 4 rows requirement
      newRowId = 1;
    }

    // Create new row for local state with default values
    // All fields get 100 except Attendance which should be empty for user to fill
    const newRow = {
      Id: newRowId,
      Week: weekName,
      DPS_Target: 100,
      CPH_Target: 100,
      GAP_Time_Target: 100,
      SIA_Target: 100,
      Attendance: 0, // Leave empty for user to fill
    };

    // Log the new row for debugging
    console.log(
      "Creating new row with default values (Attendance empty):",
      newRow
    );

    // Make PUT API call to add the new row
    if (locationName) {
      // Create payload for the API call
      const postPayload = {
        Location_Name: locationName,
        DPS_Target: 100,
        CPH_Target: 100,
        GAP_Time_Target: 100,
        SIA_Target: 100,
        Attendance: 0, // Leave as 0 for user to fill
      };

      console.log(
        "AdminTargetsTable: Making PUT API call to add row",
        postPayload
      );

      // Dispatch the API action to add a new row using PUT method
      dispatch(addAdminTargetPost(postPayload));

      // Also update the UI immediately for better UX (optimistic update)
      const newTableData = [...tableData, newRow];
      setTableData(newTableData);

      // Track that this row was added by the user
      const newUserAddedRows = [...userAddedRows, tableData.length];
      setUserAddedRows(newUserAddedRows);

      // Notify parent
      if (onDataChange) {
        console.log(
          "AdminTargetsTable: Notifying parent of data change after adding row"
        );
        onDataChange(newTableData);
      }

      // Data has changed due to user action
      setHasChanges(true);
    } else {
      console.error("Cannot add row: Location name is not available");
    }
  };

  // Handle deleting the last row (with API call)
  const handleDeleteLastRow = () => {
    // Prevent deletion if we only have 4 rows (minimum required)
    if (tableData.length <= 4) {
      return;
    }

    // Get the last row
    const lastRowIndex = tableData.length - 1;
    const lastRow = tableData[lastRowIndex];

    // If the row has an ID and onDeleteTarget is provided, make API call
    if (lastRow.Id && onDeleteTarget && locationName) {
      console.log(
        "Deleting row via API:",
        lastRow.Id,
        "at location:",
        locationName
      );

      // Call the parent delete handler which will trigger the DELETE API call
      // Pass both the ID and location name to ensure proper API call with Location_Name parameter
      onDeleteTarget(lastRow.Id, locationName);

      // Also update UI immediately for better UX (optimistic update)
      const newTableData = tableData.slice(0, -1);
      setTableData(newTableData);

      // Notify parent of the immediate UI change
      if (onDataChange) {
        onDataChange(newTableData);
      }

      // Mark as having changes
      const isDataChanged =
        JSON.stringify(newTableData) !==
        JSON.stringify(originalDataRef.current);
      setHasChanges(isDataChanged);
    } else if (lastRow.Id && onDeleteTarget && !locationName) {
      // Handle case where location name is not available
      console.error(
        "Cannot delete row: Location name is not available from Redux store"
      );
      return;
    } else {
      // For locally added rows without an ID, handle deletion locally only
      console.log("Deleting local row (no API call needed)");

      const newTableData = tableData.slice(0, -1);
      setTableData(newTableData);

      // Remove from user-added rows tracking
      if (userAddedRows.includes(lastRowIndex)) {
        const newUserAddedRows = userAddedRows.filter(
          (idx) => idx !== lastRowIndex
        );
        const adjustedUserAddedRows = newUserAddedRows.map((idx) =>
          idx > lastRowIndex ? idx - 1 : idx
        );
        setUserAddedRows(adjustedUserAddedRows);
      }

      // Notify parent
      if (onDataChange) {
        onDataChange(newTableData);
      }

      // Mark as having changes
      const isDataChanged =
        JSON.stringify(newTableData) !==
        JSON.stringify(originalDataRef.current);
      setHasChanges(isDataChanged);
    }
  };

  // Render a cell with appropriate styling
  const renderCell = (rowIndex: number, field: keyof AdminTarget) => {
    const item = tableData[rowIndex];

    if (!item) {
      return <Text>-</Text>;
    }

    // Week column is read-only
    if (field === "Week") {
      const weekValue = item[field] as string;
      const transformedWeek = weekValue.replace("Week", "Week ");
      return <Text style={[styles.cellText, { color: colors.text.primary }]}>{transformedWeek}</Text>;
    }

    // For all other fields, render an editable input
    return (
      <TextInput
        style={[
          styles.input,
          { color: colors.text.primary },
          isDark && {
            backgroundColor: colors.background.elevated,
            borderColor: colors.border.default,
          },
        ]}
        placeholderTextColor={colors.text.secondary}
        value={
          field === "Attendance" &&
          (item[field] === 0 ||
            item[field] === null ||
            item[field] === undefined)
            ? "" // Show empty field for Attendance when value is 0/null/undefined
            : item[field]?.toString() || "0"
        }
        keyboardType="numeric"
        onChangeText={(value) => handleInputChange(value, rowIndex, field)}
        maxLength={3} // Limit to 3 digits (0-100)
        testID={`${testID}-input-${rowIndex}-${field}`}
        placeholder={""}
      />
    );
  };

  // Generate column headers
  const columnHeaders = [
    { key: "Week", label: "Week" },
    { key: "DPS_Target", label: "DPS Target" },
    { key: "CPH_Target", label: "CPH Target" },
    { key: "GAP_Time_Target", label: "GAP Time Target" },
    { key: "SIA_Target", label: "SIA Target" },
    { key: "Attendance", label: "Attendance Tolerance" },
  ];

  return (
    <View testID={testID}>
      {isIOS ? (
        <ScrollView horizontal showsHorizontalScrollIndicator={true}>
          <View style={[styles.container, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
                {/* Table Header */}
                <View style={styles.headerRow}>
                  {columnHeaders.map((column) => (
                    <View
                      key={column.key}
                      style={column.key === "Week" ? styles.weekHeader : styles.headerCell}
                    >
                      <Text style={styles.headerText}>{column.label}</Text>
                    </View>
                  ))}
                </View>
    
                {/* Table Body */}
                <View style={styles.tableBody}>
                  {tableData.map((row, rowIndex) => (
                    <View
                      key={`row-${rowIndex}`}
                      style={[
                        styles.row,
                        rowIndex % 2 === 1 && styles.alternatingRow,
                        isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.default },
                        isDark && rowIndex % 2 === 1 && { backgroundColor: colors.background.elevated },
                      ]}
                    >
                      {columnHeaders.map((column) => (
                        <View
                          key={`cell-${rowIndex}-${column.key}`}
                          style={[
                            column.key === "Week" ? styles.weekCell : styles.cell,
                            isDark && column.key === "Week" && { backgroundColor: colors.background.surface, borderRightColor: colors.border.default },
                            isDark && column.key !== "Week" && { borderRightColor: colors.border.default },
                          ]}
                        >
                          {renderCell(rowIndex, column.key as keyof AdminTarget)}
                        </View>
                      ))}
                    </View>
                  ))}
                </View>
              </View>
            </ScrollView>
          ) : (
            <View style={[styles.container, isDark && { backgroundColor: colors.background.surface, borderColor: colors.border.default }]}>
              {/* Table Header */}
              <View style={styles.headerRow}>
                {columnHeaders.map((column) => (
                  <View
                    key={column.key}
                    style={column.key === "Week" ? styles.weekHeader : styles.headerCell}
                  >
                    <Text style={styles.headerText}>{column.label}</Text>
                  </View>
                ))}
              </View>
    
              {/* Table Body */}
              <View style={styles.tableBody}>
                {tableData.map((row, rowIndex) => (
                  <View
                    key={`row-${rowIndex}`}
                    style={[
                      styles.row,
                      rowIndex % 2 === 1 && styles.alternatingRow,
                      isDark && { backgroundColor: colors.background.surface, borderBottomColor: colors.border.default },
                      isDark && rowIndex % 2 === 1 && { backgroundColor: colors.background.elevated },
                    ]}
                  >
                    {columnHeaders.map((column) => (
                      <View
                        key={`cell-${rowIndex}-${column.key}`}
                        style={[
                          column.key === "Week" ? styles.weekCell : styles.cell,
                          isDark && column.key === "Week" && { backgroundColor: colors.background.surface, borderRightColor: colors.border.default },
                          isDark && column.key !== "Week" && { borderRightColor: colors.border.default },
                        ]}
                      >
                        {renderCell(rowIndex, column.key as keyof AdminTarget)}
                      </View>
                    ))}
                  </View>
                ))}
              </View>
            </View>
      )}

      {/* Table Action Buttons */}
      <View
        style={[
          styles.actionButtonsContainer,
          { backgroundColor: colors.background.surface },
          isMobile && { flexDirection: "column" },
        ]}
      >
        <View
          style={[
            styles.leftButtonsContainer,
            isMobile && {
              justifyContent: "center",
              marginBottom: 10,
              width: "100%",
            },
          ]}
        >
          <Pressable
            style={({ pressed }) => [
              styles.addButton,
              { backgroundColor: colors.background.surface, borderColor: colors.brand.primary },
              (addApiLoading || tableData.length >= 52) && styles.disabledButton,
              (isAddHovered || pressed) && !(addApiLoading || tableData.length >= 52) && styles.hoverButton,
              isMobile && { flex: 1, maxWidth: 160 },
            ]}
            onPress={handleAddRow}
            disabled={addApiLoading || tableData.length >= 52}
            onHoverIn={() => setIsAddHovered(true)}
            onHoverOut={() => setIsAddHovered(false)}
            testID={`${testID}-add-row-button`}
          >
            {addApiLoading ? (
              <View style={styles.loadingContainer}>
                <MaterialIcons name="hourglass-empty" size={16} color="#FFFFFF" />
                <Text style={[styles.buttonText, styles.disabledButtonText]}>Adding...</Text>
              </View>
            ) : (
              <>
                <MaterialIcons
                  name="add"
                  size={16}
                  color={
                    (isAddHovered && !addApiLoading && tableData.length < 52) || addApiLoading || tableData.length >= 52
                      ? "#FFFFFF"
                      : colors.brand.primary
                  }
                />
                <Text style={[
                  styles.buttonText,
                  { color: colors.brand.primary },
                  (addApiLoading || tableData.length >= 52) && styles.disabledButtonText,
                  (isAddHovered && !addApiLoading && tableData.length < 52) && styles.buttonTextHover,
                ]}>
                  {isMobile ? "Add Week" : "Add Week"}
                </Text>
              </>
            )}
          </Pressable>

          <Pressable
            style={({ pressed }) => [
              styles.deleteButton,
              { backgroundColor: colors.background.surface, borderColor: colors.brand.primary },
              (deleteLoading || tableData.length <= 4) && styles.disabledButton,
              (isDeleteHovered || pressed) && !(deleteLoading || tableData.length <= 4) && styles.hoverButton,
              isMobile && { flex: 1, maxWidth: 160 },
            ]}
            onPress={handleDeleteLastRow}
            disabled={deleteLoading || tableData.length <= 4}
            onHoverIn={() => setIsDeleteHovered(true)}
            onHoverOut={() => setIsDeleteHovered(false)}
            testID={`${testID}-delete-row-button`}
          >
            {deleteLoading ? (
              <View style={styles.loadingContainer}>
                <MaterialIcons name="hourglass-empty" size={16} color="#FFFFFF" />
                <Text style={[styles.buttonText, styles.disabledButtonText]}>Deleting...</Text>
              </View>
            ) : (
              <>
                <MaterialIcons
                  name="remove"
                  size={16}
                  color={
                    (isDeleteHovered && !deleteLoading && tableData.length > 4) || deleteLoading || tableData.length <= 4
                      ? "#FFFFFF"
                      : colors.brand.primary
                  }
                />
                <Text style={[
                  styles.buttonText,
                  { color: colors.brand.primary },
                  (deleteLoading || tableData.length <= 4) && styles.disabledButtonText,
                  (isDeleteHovered && !deleteLoading && tableData.length > 4) && styles.buttonTextHover,
                ]}>
                  {isMobile
                    ? "Delete Last Row"
                    : tableData.length <= 4
                    ? "Delete Last Row"
                    : "Delete Last Row"}
                </Text>
              </>
            )}
          </Pressable>
        </View>

        {onSaveChanges && (
          <View
            style={[
              styles.rightButtonsContainer,
              isMobile && {
                justifyContent: "center",
                width: "100%",
              },
            ]}
          >
            <TouchableOpacity
              style={[
                styles.saveButton,
                (saveLoading || !hasChanges) && styles.disabledButton,
                isMobile && { width: "100%", maxWidth: 320 },
              ]}
              onPress={onSaveChanges}
              disabled={saveLoading || !hasChanges}
              testID={`${testID}-save-button`}
            >
              {saveLoading ? (
                <View style={styles.loadingContainer}>
                  <MaterialIcons
                    name="hourglass-empty"
                    size={16}
                    color="#FFFFFF"
                  />
                <Text style={[styles.buttonText, styles.disabledButtonText]}>Saving...</Text>
                </View>
              ) : (
                <>
                  <MaterialIcons name="save" size={16} color="#FFFFFF" />
                  <Text style={[styles.buttonText1, (saveLoading || !hasChanges) && styles.disabledButtonText]}>
                    {hasChanges ? "Save Changes" : "Save Changes"}
                  </Text>
                </>
              )}
            </TouchableOpacity>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    borderWidth: 1,
    borderColor: "#ddd",
    borderRadius: 4,
    overflow: "hidden",
    width: "100%",
    backgroundColor: "white",
  },
  headerRow: {
    flexDirection: "row",
    backgroundColor: "#3582E6",
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
  },
  disabledButton: {
    backgroundColor: "#6c757d",
    opacity: 1,
  },
  disabledButtonText: {
    color: "#FFFFFF",
  },
  headerCell: {
    // width for non-week headers to match .cell width
    width: 160,
    padding: 12,
    alignItems: "center",
    borderRightWidth: 1,
    borderRightColor: "#eee",
  },

  // Header cell specifically for Week column to match weekCell width
  weekHeader: {
    minWidth: 100,
    width: 140,
    padding: 12,
    alignItems: "center",
    borderRightWidth: 1,
    borderRightColor: "#ddd",
  },
  headerText: {
    fontWeight: "600",
    fontSize: 14,
    color: "white",
  },
  tableBody: {
    width: "100%",
    borderRadius: 4,
  },
  row: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#eee",
    backgroundColor: "#ffffff",
  },
  alternatingRow: {
    //backgroundColor: "#f8f9fa",
  },
  cell: {
    //minWidth: 140,
    width: 160,
    padding: 8,
    alignItems: "center",
    justifyContent: "center",
    borderRightWidth: 1,
    borderRightColor: "#eee",
  },
  weekCell: {
    minWidth: 100,
    width: 140,
    padding: 8,
    backgroundColor: "white",
    alignItems: "center",
    justifyContent: "center",
    borderRightWidth: 1,
    borderRightColor: "#ddd",
  },
  cellText: {
    fontSize: 14,
    fontWeight: "500",
    // color set dynamically via inline style (theme-aware)
    fontFamily: FontFamily.regular,
  },
  input: {
    width: "50%",
    fontSize: 14,
    // color set dynamically via inline style (theme-aware)
    padding: Platform.OS === "web" ? 8 : 4,
    textAlign: "center",
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
    backgroundColor: "#fff",
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginTop: 0,
    width: "100%",
    alignItems: "center",
    // backgroundColor set dynamically via inline style (theme-aware)
    padding: 20,
  },
  leftButtonsContainer: {
    flexDirection: "row",
    gap: 8,
  },
  rightButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  addButton: {
    // backgroundColor and borderColor set dynamically via inline style (theme-aware)
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    paddingHorizontal: 16,
    borderRadius: 24,
    justifyContent: "center",
    minWidth: 100,
    borderWidth: 1,
  },
  deleteButton: {
    // backgroundColor and borderColor set dynamically via inline style (theme-aware)
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    paddingHorizontal: 16,
    borderRadius: 24,
    justifyContent: "center",
    minWidth: 100,
    borderWidth: 1,
  },
  saveButton: {
    backgroundColor: "#034895",
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    paddingHorizontal: 16,
    borderRadius: 24,
    justifyContent: "center",
    minWidth: 120,
  },
  // Hover style applied to add/delete buttons on web when enabled
  hoverButton: {
    backgroundColor: "#034895",
  },
  buttonText: {
    // color set dynamically via inline style (theme-aware brand primary)
    fontWeight: "500",
    marginLeft: 4,
    textAlign: "center",
  },
  buttonTextHover: {
    color: "white",
  },
  buttonText1: {
    color: "#FFFFFF",
    fontWeight: "500",
    marginLeft: 4,
    textAlign: "center",
  },
  loadingContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
});

export default AdminTargetsTable;
